import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useQuery, ApolloError } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import merge from 'lodash.merge';

import { isPartnerLegalNameIncomplete, pathIsPublic } from './util';
import { initialPermissions, TUserPermissions } from '/packages/shared-components/utils/user';
import { hasGraphQLError, hasMagic } from '/packages/shared-components/utils/util';
import { MagicForm, TBuild, TBuildName } from '/packages/shared-components/MagicForm/MagicForm';
import { allWorkstreams } from '~src/components/account/management/util/utils';

import CURRENT_USER from '~src/queries/codegen/getCurrentUser';
import { getCurrentUserQuery, UserType } from '~src/generated/portal/graphql';

type QUser = getCurrentUserQuery['currentUser'];

const initialContext = {
  currentUser: undefined,
  loadingUser: true,
  userFetchError: undefined,
  refetchCurrentUser: () => null,
  permissions: initialPermissions,
};

interface UserContextProps {
  currentUser?: QUser;
  loadingUser: boolean;
  userFetchError?: ApolloError;
  refetchCurrentUser: () => void;
  permissions: TUserPermissions;
  build?: Record<TBuildName, TBuild>;
}

export const UserContext = React.createContext<UserContextProps>(initialContext);

export const useUser = () => useContext(UserContext);

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const lastSegment = window.location.pathname.substring(window.location.pathname.lastIndexOf('/') + 1);
  const isMagic = hasMagic();
  const [lastMagic, setLastMagic] = useState<Date>(() => new Date());
  const [build, setBuild] = useState<Record<string, TBuild>>({});

  const {
    data: { currentUser } = {},
    loading: loadingUser,
    error: userFetchError,
    refetch: refetchCurrentUser,
  } = useQuery(CURRENT_USER, {
    skip: pathIsPublic(lastSegment),
  });

  useEffect(() => {
    const fetchBuildInfo = (url: string): Promise<TBuild> =>
      fetch(url)
        .then((res) => (res.ok ? res.json() : { error: `HTTP ${res.status}` }))
        .catch((err) => ({ error: err.message }));

    const adkBaseUrl = (process.env.REACT_APP_WEBSOCKET_URI || '').replace(/^wss?:/, 'https:');

    Promise.all([
      fetchBuildInfo(process.env.NODE_ENV === 'development' ? '/build-info.json' : '/app/build-info.json'),
      fetchBuildInfo('/support/build-info.json'),
      fetchBuildInfo(`${process.env.REACT_APP_API_BASE_URI}/build-info.json`),
      fetchBuildInfo(`${adkBaseUrl}/build-info.json`),
    ]).then(([webapp, mira, api, adk]) => {
      setBuild({ webapp, mira, api, adk });
    });
  }, []);

  // Set FullStory user data
  useEffect(() => {
    const initializeFullStory = async () => {
      // const isPortalQA = window.location.hostname.includes('portal-qa');
      const isPortalQA = true; // TODO: Replace with actual check

      if (isPortalQA && currentUser?.id) {
        // Load FullStory script first
        const fsScript = document.createElement('script');
        fsScript.type = 'text/javascript';
        fsScript.text = `
        // Begins FullStory JS snippet - Do not change this
        window['_fs_host'] = 'fullstory.com';
        window['_fs_script'] = 'edge.fullstory.com/s/fs.js';
        window['_fs_org'] = 'o-1CVHDB-na1';
        window['_fs_namespace'] = 'FS';
        !(function (m, n, e, t, l, o, g, y) {
          var s,
            f,
            a = (function (h) {
              return (
                !(h in m) ||
                (m.console &&
                  m.console.log &&
                  m.console.log('FullStory namespace conflict. Please set window["_fs_namespace"].'),
                !1)
              );
            })(e);
          function p(b) {
            var h,
              d = [];
            function j() {
              h &&
                (d.forEach(function (b) {
                  var d;
                  try {
                    d = b[h[0]] && b[h[0]](h[1]);
                  } catch (h) {
                    return void (b[3] && b[3](h));
                  }
                  d && d.then ? d.then(b[2], b[3]) : b[2] && b[2](d);
                }),
                (d.length = 0));
            }
            function r(b) {
              return function (d) {
                h || ((h = [b, d]), j());
              };
            }
            return (
              b(r(0), r(1)),
              {
                then: function (b, h) {
                  return p(function (r, i) {
                    d.push([b, h, r, i]), j();
                  });
                },
              }
            );
          }
          a &&
            ((g = m[e] =
              (function () {
                var b = function (b, d, j, r) {
                  function i(i, c) {
                    h(b, d, j, i, c, r);
                  }
                  r = r || 2;
                  var c,
                    u = /Async$/;
                  return u.test(b)
                    ? ((b = b.replace(u, '')), 'function' == typeof Promise ? new Promise(i) : p(i))
                    : h(b, d, j, c, c, r);
                };
                function h(h, d, j, r, i, c) {
                  return b._api ? b._api(h, d, j, r, i, c) : (b.q && b.q.push([h, d, j, r, i, c]), null);
                }
                return (b.q = []), b;
              })()),
            (y = function (b) {
              function h(h) {
                'function' == typeof h[4] && h[4](new Error(b));
              }
              var d = g.q;
              if (d) {
                for (var j = 0; j < d.length; j++) h(d[j]);
                (d.length = 0), (d.push = h);
              }
            }),
            (function () {
              ((o = n.createElement(t)).async = !0),
                (o.crossOrigin = 'anonymous'),
                (o.src = 'https://' + l),
                (o.onerror = function () {
                  y('Error loading ' + l);
                });
              var b = n.getElementsByTagName(t)[0];
              b && b.parentNode ? b.parentNode.insertBefore(o, b) : n.head.appendChild(o);
            })(),
            (function () {
              function b() {}
              function h(b, h, d) {
                g(b, h, d, 1);
              }
              function d(b, d, j) {
                h('setProperties', { type: b, properties: d }, j);
              }
              function j(b, h) {
                d('user', b, h);
              }
              function r(b, h, d) {
                j(
                  {
                    uid: b,
                  },
                  d,
                ),
                  h && j(h, d);
              }
              (g.identify = r),
                (g.setUserVars = j),
                (g.identifyAccount = b),
                (g.clearUserCookie = b),
                (g.setVars = d),
                (g.event = function (b, d, j) {
                  h(
                    'trackEvent',
                    {
                      name: b,
                      properties: d,
                    },
                    j,
                  );
                }),
                (g.anonymize = function () {
                  r(!1);
                }),
                (g.shutdown = function () {
                  h('shutdown');
                }),
                (g.restart = function () {
                  h('restart');
                }),
                (g.log = function (b, d) {
                  h('log', { level: b, msg: d });
                }),
                (g.consent = function (b) {
                  h('setIdentity', { consent: !arguments.length || b });
                });
            })(),
            (s = 'fetch'),
            (f = 'XMLHttpRequest'),
            (g._w = {}),
            (g._w[f] = m[f]),
            (g._w[s] = m[s]),
            m[s] &&
              (m[s] = function () {
                return g._w[s].apply(this, arguments);
              }),
            (g._v = '2.0.0'));
        })(window, document, window._fs_namespace, 'script', window._fs_script);`;

        document.head.appendChild(fsScript);

        // Wait for FullStory to load
        await new Promise<void>((resolve) => {
          const checkFS = () => {
            if ((window as any).FS) {
              resolve();
            } else {
              setTimeout(checkFS, 100);
            }
          };
          checkFS();
        });

        // Set user data after FullStory is loaded
        const userScript = document.createElement('script');
        userScript.type = 'text/javascript';
        userScript.text = `FS.identify('${currentUser.id}', {
        id: '${currentUser.id}',
      });`;

        document.body.appendChild(userScript);
      }
    };

    const pathname = window.location.pathname;

    if (!currentUser?.id) {
      return;
    }

    if (pathname.includes('/complete-partner-profile')) {
      return;
    }

    if (currentUser.fullStoryDisclosureRequired) {
      if (!isPartnerLegalNameIncomplete(currentUser) && !pathname.includes('/full-story-disclosure')) {
        navigate('/app/full-story-disclosure');
      }
    } else {
      initializeFullStory();
    }
  }, [currentUser?.id, currentUser?.fullStoryDisclosureRequired]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (userFetchError) {
      const isUnauthorized = hasGraphQLError(userFetchError.graphQLErrors, 'Unauthorized');

      if (!isUnauthorized) console.error(userFetchError?.message);
    }
  }, [userFetchError]); // eslint-disable-line react-hooks/exhaustive-deps

  // Memo provider value
  const userValue = useMemo(
    () => ({
      currentUser,
      loadingUser,
      userFetchError,
      refetchCurrentUser,
      permissions: getPermissions(currentUser, isMagic),
      version: build,
    }),
    [currentUser, loadingUser, userFetchError, refetchCurrentUser, isMagic, lastMagic],
  );

  return (
    <UserContext.Provider value={userValue}>
      {children}
      {process.env.MAGIC === 'true' && (
        <MagicForm
          storageKey="portal:permissions"
          defaultValues={getPermissions(currentUser, true)}
          view="partner"
          setLastMagic={setLastMagic}
          workstreams={allWorkstreams.map((ws) => ws.value)}
        />
      )}
    </UserContext.Provider>
  );
};

function buildCodeAccessMap(user: QUser): Record<string, boolean> {
  const persona = user?.userPermissions?.persona;
  if (!persona) return {};

  if (persona.__typename === 'DisneyAdmin') return { '*': true };

  const perms =
    persona.__typename === 'PartnerUser'
      ? persona.partnerPermissions
      : persona.__typename === 'DisneyUser'
      ? persona.spacePermissions
      : [];

  const map: Record<string, boolean> = {};
  perms?.forEach((perm) => {
    if (perm.__typename === 'PartnerPermission') {
      const has = perm.componentPermissions?.some(
        (p) => p.__typename === 'ADKSourceCodePermission' && p.sourceCodeAccess,
      );
      if (has) map[perm.partnerId] = true;
    }
  });

  return map;
}

function buildContentProviderAdditionalFieldsMap(user: QUser): Record<string, boolean> {
  const persona = user?.userPermissions?.persona;
  if (!persona) return {};

  if (persona.__typename === 'DisneyAdmin') return { '*': true };

  const perms =
    persona.__typename === 'PartnerUser'
      ? persona.partnerPermissions
      : persona.__typename === 'DisneyUser'
      ? persona.spacePermissions
      : [];

  const map: Record<string, boolean> = {};
  perms?.forEach((perm) => {
    if (perm.__typename === 'PartnerPermission') {
      const has = perm.componentPermissions?.some(
        (p) => p.__typename === 'ContentProviderAdditionalFieldsPermission' && p.contentProviderAdditionalFieldsAccess,
      );
      if (has) map[perm.partnerId] = true;
    }
  });

  return map;
}

function buildEntitlementPushBasedApiMap(user: QUser): Record<string, boolean> {
  const persona = user?.userPermissions?.persona;
  if (!persona) return {};

  if (persona.__typename === 'DisneyAdmin') return { '*': true };

  const perms =
    persona.__typename === 'PartnerUser'
      ? persona.partnerPermissions
      : persona.__typename === 'DisneyUser'
      ? persona.spacePermissions
      : [];

  const map: Record<string, boolean> = {};
  perms?.forEach((perm) => {
    if (perm.__typename === 'PartnerPermission') {
      const has = perm.componentPermissions?.some(
        (p) => p.__typename === 'EntitlementPushBasedApiPermission' && p.entitlementPushBasedApiAccess,
      );
      if (has) map[perm.partnerId] = true;
    }
  });

  return map;
}

function buildRefundsAndChargebacksPushBasedApiMap(user: QUser): Record<string, boolean> {
  const persona = user?.userPermissions?.persona;
  if (!persona) return {};

  if (persona.__typename === 'DisneyAdmin') return { '*': true };

  const perms =
    persona.__typename === 'PartnerUser'
      ? persona.partnerPermissions
      : persona.__typename === 'DisneyUser'
      ? persona.spacePermissions
      : [];

  const map: Record<string, boolean> = {};
  perms?.forEach((perm) => {
    if (perm.__typename === 'PartnerPermission') {
      const has = perm.componentPermissions?.some(
        (p) =>
          p.__typename === 'RefundsAndChargebacksPushBasedApiPermission' && p.refundsAndChargebacksPushBasedApiAccess,
      );
      if (has) map[perm.partnerId] = true;
    }
  });

  return map;
}

function buildOnDeviceActivationMap(user: QUser): Record<string, boolean> {
  const persona = user?.userPermissions?.persona;
  if (!persona) return {};

  if (persona.__typename === 'DisneyAdmin') return { '*': true };

  const perms =
    persona.__typename === 'PartnerUser'
      ? persona.partnerPermissions
      : persona.__typename === 'DisneyUser'
      ? persona.spacePermissions
      : [];

  const map: Record<string, boolean> = {};
  perms?.forEach((perm) => {
    if (perm.__typename === 'PartnerPermission') {
      const has = perm.componentPermissions?.some(
        (p) => p.__typename === 'OnDeviceActivationPermission' && p.onDeviceActivationAccess,
      );
      if (has) map[perm.partnerId] = true;
    }
  });

  return map;
}

function getPermissions(user: QUser, isMagic = false): TUserPermissions {
  if (!user) return initialPermissions;
  const isDisney = user.userType === UserType.Disney;
  const permissions: TUserPermissions = {
    accessManagement: {
      admin: isDisney && user.isAccessManagementAdmin,
    },
    adk: {
      changesRequested: isDisney,
      createProject: isDisney,
      requestProject: user.userType === UserType.Partner,
      reviewProject: isDisney,
      admin: isDisney,
      codeAccess: buildCodeAccessMap(user),
      onDeviceActivation: buildOnDeviceActivationMap(user),
    },
    contentProvider: {
      additionalFields: buildContentProviderAdditionalFieldsMap(user),
      entitlementPushBasedApi: buildEntitlementPushBasedApiMap(user),
      refundsAndChargebacksPushBasedApi: buildRefundsAndChargebacksPushBasedApiMap(user),
    },
    perks: { admin: user.isPerksManagementAdmin },
    android: {
      admin: isDisney,
    },
    api: {
      sku: isDisney,
      admin: isDisney,
    },
    admin: !!user.isPortalAdmin,
    isDisney,
    isPartner: user.userType === UserType.Partner,
    services: isDisney && !!user.isPortalAdmin,
    switchPartner: user.partnerWorkstreams.length > 1,
    workstreams: user.selectedPartner?.workstreams ?? [],
  };

  if (process.env.MAGIC === 'true' && isMagic) {
    const storageLocalPermissions = sessionStorage.getItem('portal:permissions');
    if (storageLocalPermissions) {
      const localPermissions: TUserPermissions = JSON.parse(storageLocalPermissions);
      const newPermissions = merge({}, permissions, localPermissions);
      return newPermissions;
    }
  }

  return permissions;
}
